import { auth } from "@/firebase/config";
import { resolveLocalDevApiUrl } from "@/utils/api/localDevApiUrl";

const pendingUserRequests = new Map<string, Promise<Response>>();

/**
 * Dynamic API client that automatically detects environment and uses correct endpoints
 */


/**
 * Create a full URL for `fetch`. Uses same-origin `/api/...` paths; webpack dev-server
 * proxies them to the Node API on :3001 when running `npm run dev`.
 */
export const createApiUrl = (endpoint: string): string => {
  const raw = (endpoint || "").trim();
  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }
  const cleanEndpoint = raw.startsWith("/") ? raw : `/${raw}`;
  return resolveLocalDevApiUrl(cleanEndpoint);
};

const canDeduplicateRequest = (
  method: string,
  init: RequestInit | undefined,
): boolean => {
  return (
    (method === "GET" || method === "HEAD") &&
    !init?.signal
  );
};

const normalizeHeadersForKey = (headersInit: HeadersInit | undefined): string => {
  if (!headersInit) {
    return "";
  }

  const headers = new Headers(headersInit);
  const headerPairs: [string, string][] = [];
  headers.forEach((value, name) => {
    headerPairs.push([name, value]);
  });
  const normalized = headerPairs
    .filter(([name]) => name.toLowerCase() !== "authorization")
    .sort(([left], [right]) => left.localeCompare(right));

  return JSON.stringify(normalized);
};

const buildRequestKey = (
  endpoint: string,
  method: string,
  init: RequestInit | undefined,
  forceRefresh: boolean,
): string => {
  const userId = auth?.currentUser?.uid || "anonymous";
  const body = typeof init?.body === "string" ? init.body : "";

  return JSON.stringify({
    userId,
    method,
    endpoint: createApiUrl(endpoint),
    body,
    headers: normalizeHeadersForKey(init?.headers),
    forceRefresh,
  });
};

const shouldRetryWithFreshToken = (
  method: string,
  status: number,
  data: unknown,
): boolean => {
  if (method !== "GET" && method !== "HEAD") {
    return false;
  }

  if (status === 401 || status === 403) {
    return true;
  }

  if (status !== 500 || !data || typeof data !== "object") {
    return false;
  }

  const error = "error" in data ? data.error : null;
  return typeof error === "string" && /token|auth|firebase/i.test(error);
};

const buildAuthorizedHeaders = async (
  init: RequestInit | undefined,
  forceRefresh: boolean,
): Promise<Headers> => {
  const user = auth?.currentUser;
  if (!user) {
    throw new Error("You must be signed in to continue.");
  }

  const headers = new Headers(init?.headers || {});
  const idToken = await user.getIdToken(forceRefresh);
  headers.set("Authorization", `Bearer ${idToken}`);

  if (init?.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  return headers;
};

/**
 * An /api/ path that matches no deployed function falls through to the SPA
 * rewrite and returns index.html with a 200. Callers then parse `{}` and fail
 * silently — the crash surfaces later, far from the cause. Treat it as a hard
 * error so the normal `!response.ok` path runs and monitoring sees it.
 */
const SPA_FALLTHROUGH_MESSAGE =
  "API route did not resolve — received the app shell instead of JSON.";

const isSpaFallthrough = (endpoint: string, response: Response): boolean => {
  if (!response.ok) {
    return false;
  }
  const path = (endpoint || "").trim();
  if (!path.startsWith("/api/") && !path.includes("/api/")) {
    return false;
  }
  return (response.headers.get("content-type") || "").includes("text/html");
};

export { isSpaFallthrough };

export const fetchWithUserAuth = async (
  endpoint: string,
  init?: RequestInit,
): Promise<Response> => {
  const method = (init?.method || "GET").toUpperCase();

  const sendRequest = async (forceRefresh: boolean): Promise<Response> => {
    try {
      const headers = await buildAuthorizedHeaders(init, forceRefresh);
      return await fetch(createApiUrl(endpoint), {
        ...init,
        headers,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") {
        throw error;
      }
      if (error instanceof Error && error.name === "AbortError") {
        throw error;
      }
      const isNetworkFailure =
        error instanceof TypeError &&
        /failed to fetch|networkerror|load failed/i.test(error.message);
      throw new Error(
        isNetworkFailure
          ? "Could not reach the API. If you are developing locally, keep npm run dev running so the API on port 3001 stays up."
          : error instanceof Error
            ? error.message
            : "Request failed",
      );
    }
  };

  const sendRequestDeduped = async (forceRefresh: boolean): Promise<Response> => {
    if (!canDeduplicateRequest(method, init)) {
      return sendRequest(forceRefresh);
    }

    const requestKey = buildRequestKey(endpoint, method, init, forceRefresh);
    const pendingRequest = pendingUserRequests.get(requestKey);
    if (pendingRequest) {
      const sharedResponse = await pendingRequest;
      return sharedResponse.clone();
    }

    const requestPromise = sendRequest(forceRefresh).finally(() => {
      pendingUserRequests.delete(requestKey);
    });

    pendingUserRequests.set(requestKey, requestPromise);

    const response = await requestPromise;
    return response.clone();
  };

  const response = await sendRequestDeduped(false);
  const data = await response.clone().json().catch(() => null);
  if (shouldRetryWithFreshToken(method, response.status, data)) {
    return sendRequestDeduped(true);
  }

  if (isSpaFallthrough(endpoint, response)) {
    reportApiClientFailure(endpoint, method, 502, {
      error: SPA_FALLTHROUGH_MESSAGE,
    });
    return new Response(JSON.stringify({ error: SPA_FALLTHROUGH_MESSAGE }), {
      status: 502,
      headers: { "Content-Type": "application/json" },
    });
  }

  return response;
};

/**
 * Report failed authenticated API calls to the monitoring pipeline so 405/4xx
 * surface in the admin portal even when callers catch and display the error.
 */
export function reportApiClientFailure(
  endpoint: string,
  method: string,
  status: number,
  data: unknown,
): void {
  if (typeof window === "undefined") {
    return;
  }

  const record =
    data && typeof data === "object"
      ? (data as Record<string, unknown>)
      : {};
  const serverError =
    typeof record.error === "string" && record.error.trim()
      ? record.error.trim()
      : "";
  const message = serverError
    ? `${method} ${endpoint} failed (${status}): ${serverError}`
    : `${method} ${endpoint} failed (${status})`;
  const type = status === 405 ? "api.method_not_allowed" : "api.client_failure";

  void fetch(createApiUrl("/api/client-error"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      type,
      message,
      url: window.location.href,
    }),
  }).catch(() => {});
};

/**
 * Stripe API endpoints
 */
export const STRIPE_ENDPOINTS = {
  CREATE_CHECKOUT_SESSION: '/api/stripe/create-checkout-session',
  CREATE_EVENT_CHECKOUT_SESSION: '/api/stripe/create-event-checkout-session',
  BILLING_PORTAL_SESSION: '/api/stripe/billing-portal-session',
  WEBHOOK: '/api/stripe/webhook'
} as const;

/**
 * Get Stripe API URLs
 */
export const getStripeApiUrl = (endpoint: keyof typeof STRIPE_ENDPOINTS): string => {
  return createApiUrl(STRIPE_ENDPOINTS[endpoint]);
};
